/**
 * The unified map's mode model (#327): which mode exists, when Forecast is
 * offered, what the camera does on a switch and which forecast config the
 * mode opens on. Pure decisions, shared by the webapp and (later) mobile.
 */
import type {ForecastMapModelDomain} from '../../api/types';
import {clampLatitude, domainFitBounds, viewCoverage} from './coverage';

export type MapMode = 'observations' | 'forecast';

/** `/map` without a `mode` param is observations, always; the mode is never persisted. */
export const DEFAULT_MAP_MODE: MapMode = 'observations';

export const isMapMode = (value: unknown): value is MapMode =>
  value === 'observations' || value === 'forecast';

// Coarse cross-model guardrail: the overlay is a fixed raster at the model's
// native grid density, so zooming far past it only magnifies grid cells.
// Cap zoom at a still-useful level rather than an all-blur close-up.
export const FORECAST_OVERLAY_MAX_ZOOM = 11;

export const MAP_MODE_SPECS: Readonly<
  Record<MapMode, {readonly maxZoom?: number}>
> = {
  observations: {},
  // The forecast overlay is a fixed raster at the model's grid density.
  forecast: {maxZoom: FORECAST_OVERLAY_MAX_ZOOM},
};

export type MapModeAvailability = {
  forecast: 'signInRequired' | 'available';
};

/**
 * Whether the Forecast mode is offered, and how: to every signed-in user
 * (#327 P: no forecast flag any more), and to an anonymous visitor behind a
 * sign-in prompt -- choosing it asks them to sign in and does not switch
 * (the unified map is everyone's /map since #327 P-B). What a free account
 * may open inside the mode is the catalog's to say (`locked` presets, #327
 * MR7a), never the account's: the entitlement covers superusers, who are
 * not Premium.
 */
export const resolveMapModeAvailability = ({
  signedIn,
}: {
  signedIn: boolean;
}): MapModeAvailability => ({
  forecast: signedIn ? 'available' : 'signInRequired',
});

/**
 * `[west, south, east, north]`, EPSG:4326, i.e. flattened
 * `getBounds().toArray()`.
 */
export type MapBounds = [number, number, number, number];

export type ModeSwitchCamera =
  | {kind: 'keep'}
  | {kind: 'fit'; bounds: MapBounds; padding: 16};

// A longitude interval with its west edge folded into [-180, 180). A domain
// that crosses the antimeridian (west > east) is unrolled east of it first.
const lonInterval = (west: number, east: number): [number, number] => {
  const unrolledEast = east < west ? east + 360 : east;
  const foldedWest = ((((west + 180) % 360) + 360) % 360) - 180;
  return [foldedWest, foldedWest + (unrolledEast - west)];
};

// Strict overlap (shared edges do not count), on the circle: maplibre
// reports longitudes beyond ±180 for world copies, and a global grid spans
// 360° without being exactly ±180.
const longitudesOverlap = (view: MapBounds, domain: MapBounds): boolean => {
  const [viewWest, viewEast] = lonInterval(view[0], view[2]);
  const [domainWest, domainEast] = lonInterval(domain[0], domain[2]);
  if (viewEast - viewWest >= 360 || domainEast - domainWest >= 360) {
    return true;
  }
  return [-360, 0, 360].some(
    (shift) => domainWest + shift < viewEast && viewWest < domainEast + shift,
  );
};

/**
 * What the camera does when forecast mode opens (or its config changes):
 * keep it while the view overlaps the model's domain, otherwise — or with no
 * camera yet (first open) — fit the domain. Fits are Mercator-clamped COPIES;
 * the rendering bounds stay unclamped (GFS spans ±90).
 *
 * `domain` is the catalog's `forecast_model.domain`:
 * - a polygon: keep on any positive-area overlap with the polygon
 *   (`viewCoverage > 0`, so an edge touch is a miss), else fit the polygon's
 *   own bounds (`domainFitBounds`, west > east across the antimeridian). The
 *   bbox would over-report: EC_RDPS's spans every longitude above 17°N, so a
 *   view over Europe would be kept on an empty, masked map.
 * - `null`, a global model: always keep, even with no camera yet — opening a
 *   global model keeps the default view rather than fitting the world.
 * - absent (an older payload): the overlap test falls back to `domainBounds`,
 *   the run's bounding box.
 */
export const cameraForModeSwitch = ({
  view,
  domainBounds,
  domain,
}: {
  view: Readonly<MapBounds> | null;
  domainBounds: Readonly<MapBounds>;
  domain?: ForecastMapModelDomain | null;
}): ModeSwitchCamera => {
  if (domain === null) {
    return {kind: 'keep'};
  }
  const hasView = view !== null && view.every(Number.isFinite);
  if (domain !== undefined) {
    return hasView && viewCoverage(view, domain) > 0
      ? {kind: 'keep'}
      : {kind: 'fit', bounds: domainFitBounds(domain), padding: 16};
  }
  const bounds: MapBounds = [...domainBounds];
  const viewOverlaps =
    hasView &&
    view[1] < bounds[3] &&
    bounds[1] < view[3] &&
    longitudesOverlap([...view], bounds);
  if (viewOverlaps) {
    return {kind: 'keep'};
  }
  return {
    kind: 'fit',
    bounds: [
      bounds[0],
      clampLatitude(bounds[1]),
      bounds[2],
      clampLatitude(bounds[3]),
    ],
    padding: 16,
  };
};

export type ForecastConfigResolution = {
  configId: number | null;
  /** The URL named a config that is not in the catalog: show the Banner. */
  invalidUrlConfig: boolean;
  /** The saved config is gone (unpublished, deleted, locked): forget it. */
  clearSaved: boolean;
};

/**
 * Which forecast config the mode opens on: the URL `config` first, then the
 * locally saved one, then the first in the catalog. A stale saved id falls
 * back silently (no Banner) and is cleared; only an invalid URL config is
 * reported. Call it with a LOADED catalog — an empty `availableIds` reads as
 * "nothing is available" and clears the saved id.
 *
 * `availableIds` must only hold configs the user may open: leave out the
 * backend's `locked: true` presets, so a locked URL or saved id falls back
 * like any other unavailable one.
 */
export const resolveForecastConfigId = ({
  urlConfigId,
  savedConfigId,
  availableIds,
}: {
  urlConfigId: number | null;
  savedConfigId: number | null;
  availableIds: readonly number[];
}): ForecastConfigResolution => {
  const urlValid = urlConfigId !== null && availableIds.includes(urlConfigId);
  const savedValid =
    savedConfigId !== null && availableIds.includes(savedConfigId);
  let configId: number | null = availableIds[0] ?? null;
  if (urlValid) {
    configId = urlConfigId;
  } else if (savedValid) {
    configId = savedConfigId;
  }
  return {
    configId,
    invalidUrlConfig: urlConfigId !== null && !urlValid,
    clearSaved: savedConfigId !== null && !savedValid,
  };
};
